import React from 'react'
import SwipeableViews from 'react-swipeable-views'
import { bindKeyboard } from 'react-swipeable-views-utils'
import styled from 'styled-components'
import { rem } from 'polished'
import { Flex, Box } from 'rebass'
import media, { MediumScreenUp, SmallScreenOnly } from '../../utils/style'
import { array } from 'prop-types'
import Avatar from '../Avatar'
import StarRating from '../StarRating'
import IconFormArrowRight from '../../vectors/IconFormArrowRight'

const BindKeyboardSwipeableViews = bindKeyboard(SwipeableViews)

/*************************
 STYLES: WRAPPER
 **************************/

const ContentFrame = styled(Flex)`
  width: 100%;
  max-width: ${(props) => props.theme.content.max.width};
  position: relative;
  z-index: 1;
  justify-content: center;

  ${media.MediumScreenUp`
    align-items: stretch;
  `}
`

const ReviewsWrapper = styled(Flex)`
  position: relative;
  cursor: pointer;
  z-index: 1;
  opacity: ${(props) => (props.id === props.index ? 1 : 0.3)};
  padding: 0 20px;
  transition: opacity 0.4s ${(props) => props.theme.ease.ease_out_expo};
  height: 90%;
  overflow: visible;
  box-shadow: 0 25px 25px 0px rgba(66, 76, 94, 0.05),
    0 20px 20px 0 rgba(0, 0, 0, 0.01);

  ${(props) =>
    props.id === props.index &&
    `
    &::before {
      content:'';
      position: absolute;
      background-color: white;
      z-index: 0;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 100%;
      height: 100%;
      box-shadow:
        0 25px 25px 0px rgba(66,76,94,0.05),
        0 20px 20px 0 rgba(0,0,0,0.01);
    }
  `}
`

const ReviewBody = styled(Flex)`
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 50px;

  div {
    margin-right: 40px;
  }

  p {
    position: relative;
    height: 140px; /* 4 lines of content */
    overflow: hidden;
  }

  p:after {
    content: '';
    text-align: right;
    position: absolute;
    bottom: 0;
    right: 0;
    width: 70%;
    height: 35.2px;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 1) 50%
    );
  }
`

const ReviewMeta = styled(Box)`
  ${media.MediumScreenUp`
  display: flex;
  justify-content: space-between;
  `}
`

const ReviewAuthor = styled.span`
  font-weight: bold;
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.blue};
  font-size: ${(props) => rem(props.theme.fontSizes[4])};
`

const Review = styled(Flex)`
  position: relative;
  width: 100%;
  background-color: white;
  flex-direction: column;
  justify-content: space-between;
`

const TransitionButton = styled.div`
  cursor: pointer;
  position: absolute;
  width: 60px;
  height: 60px;
  top: 0;
  right: 15%;
  background: ${(props) => props.theme.colors.blue};

  &:hover {
    background: ${(props) => props.theme.colors.grey};
  }

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`

const TransitionNext = styled(TransitionButton)`
  transform: translate(50px, 0);
`

const TransitionPrev = styled(TransitionButton)`
  transform: translate(-10px, 0);
  svg {
    transform: translate(-50%, -50%) rotate(180deg);
  }
`

class ContentReviewCards extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = { index: 0 }
    this._next = this._next.bind(this)
    this._prev = this._prev.bind(this)
    this._nudgeLeft = this._nudgeLeft.bind(this)
    this._nudgeRight = this._nudgeRight.bind(this)
    this._nudgeDefault = this._nudgeDefault.bind(this)
    this._handleChangeIndex = this._handleChangeIndex.bind(this)
  }

  _handleChangeIndex(index) {
    this.setState({
      index,
      nudge: null
    })
  }

  _nudgeLeft(_e) {
    if (_e) _e.preventDefault()
    this.setState({
      nudge: 'review__nudge-left'
    })
  }

  _nudgeRight(_e) {
    if (_e) _e.preventDefault()
    this.setState({
      nudge: 'review__nudge-right'
    })
  }

  _nudgeDefault(_e) {
    if (_e) _e.preventDefault()
    this.setState({
      nudge: null
    })
  }

  _next(_e) {
    if (_e) _e.preventDefault()
    this._handleChangeIndex(this.state.index + 1)
  }

  _prev(_e) {
    if (_e) _e.preventDefault()
    this._handleChangeIndex(this.state.index - 1)
  }

  render() {
    const reviews = this.props.reviews
    const desktopStyles = {
      root: {
        width: 'calc(70% - 20px)',
        overflow: 'visible'
      },
      slideContainer: {
        width: '100%',
        overflow: 'visible'
      }
    }

    const mobileStyles = {
      root: {
        width: 'calc(90% - 20px)',
        overflow: 'visible'
      },
      slideContainer: {
        width: '100%',
        overflow: 'visible'
      }
    }

    const reviewCards = reviews.map((review, index) => {
      const initials = review.name
        .split(' ')
        .map((name) => name[0])
        .join('')
      return (
        <ReviewsWrapper
          width={[null, null, 12 / 12]}
          key={index}
          id={index}
          index={this.state.index}
          className={'transition-preview'}
        >
          <Review
            pl={[10, 40, 40]}
            pr={[10, 40, 40]}
            pt={[30, 60]}
            pb={[30, 40, 70]}
          >
            <ReviewBody>
              <MediumScreenUp>
                <Avatar initials={initials} />
              </MediumScreenUp>
              <p>{review.content}</p>
            </ReviewBody>
            <ReviewMeta>
              <StarRating value={review.rating} />
              <ReviewAuthor>{review.name}</ReviewAuthor>
            </ReviewMeta>
          </Review>
        </ReviewsWrapper>
      )
    })

    return (
      <ContentFrame mt={[0, 0, 0, 10]} mb={[20, 80]} bg="white" as="article">
        <SmallScreenOnly>
          <BindKeyboardSwipeableViews
            enableMouseEvents
            index={this.state.index}
            onChangeIndex={this._handleChangeIndex}
            style={mobileStyles.root}
            slideStyle={mobileStyles.slideContainer}
            className={this.state.nudge}
          >
            {reviewCards}
          </BindKeyboardSwipeableViews>
        </SmallScreenOnly>
        <MediumScreenUp>
          <BindKeyboardSwipeableViews
            enableMouseEvents
            index={this.state.index}
            onChangeIndex={this._handleChangeIndex}
            style={desktopStyles.root}
            slideStyle={desktopStyles.slideContainer}
            className={this.state.nudge}
          >
            {reviewCards}
          </BindKeyboardSwipeableViews>
          {this.state.index < reviews.length - 1 && (
            <TransitionNext
              id="transition-next"
              onClick={this._next}
              onMouseEnter={this._nudgeRight}
              onMouseOut={this._nudgeDefault}
            >
              <IconFormArrowRight />
            </TransitionNext>
          )}
          {this.state.index > 0 && (
            <TransitionPrev
              id="transition-prev"
              onClick={this._prev}
              onMouseEnter={this._nudgeLeft}
              onMouseOut={this._nudgeDefault}
            >
              <IconFormArrowRight />
            </TransitionPrev>
          )}
        </MediumScreenUp>
      </ContentFrame>
    )
  }
}

ContentReviewCards.defaultProps = {
  reviews: []
}
ContentReviewCards.propTypes = {
  reviews: array
}

export default ContentReviewCards
